import { apiClient } from 'api/apiClient';
import { ParentAccountsResponse } from 'api/techexpress/schemas/ParentAccountsResponse';
import {
  CreateLocationForOrder,
  CreateOrderItem,
  DraftOrderAPIResponse,
  MoveOrderItems,
  OrderResponse,
  OrdersAPIResponseSchema,
  OrdersFilterSearchParams,
  OrdersFilterSearchParamsSchema,
  OrdersIndexAPIResponse,
  PostDraftRequest,
  PostOrderItems,
  UpdateOrderItem,
} from './schemas/OrdersSchemas';
import { prepareSortingParams } from 'api/common-schemas';
import { CreateQuoteRequest } from './schemas/QuotesSchemas';
import { AxiosRequestConfig } from 'axios';
import {
  CompanyPreferencesRequest,
  CompanyPreferencesResponse,
} from 'screens/AccessExpressV3/BaseComponents/Cart/locationFlow/access/ConfigRequirementsModal/schema';

export const getAccessParentAccounts = async (): Promise<
  ParentAccountsResponse[]
> => {
  const response = await apiClient.get('/api/v1/orders/parents');
  return response.data?.data;
};

export const postCompanyPreferences = async (
  data: CompanyPreferencesRequest,
): Promise<CompanyPreferencesResponse> => {
  const response = await apiClient.post(
    '/api/v1/access/company/preferences',
    data,
  );
  return response.data;
};

export const getCompanyPreferences =
  async (): Promise<CompanyPreferencesResponse> => {
    const response = await apiClient.get('/api/v1/access/company/preferences');
    return response.data;
  };

const mapOrderColumns = {
  order_name_order: 'order_name',
  requester_order: 'requester',
  order_number_order: 'order_number',
  updated_at_order: 'updated_at',
  created_at_order: 'created_at',
};

export const getOrdersIndex = async (
  params: OrdersFilterSearchParams = {},
): Promise<OrdersIndexAPIResponse> => {
  const newQueryParams = prepareSortingParams(
    params,
    mapOrderColumns,
    OrdersFilterSearchParamsSchema,
  );

  const response = await apiClient.get('/api/v1/orders', {
    params: {
      ...newQueryParams,
      sort_field: newQueryParams.sort_column,
    },
  });
  return OrdersAPIResponseSchema.parse(response.data);
};

export const getOrderDetails = async (id: string): Promise<OrderResponse> => {
  const response = await apiClient.get<OrderResponse>(`/api/v1/orders/${id}`);
  return response.data;
};

export const getLastDraftOrder =
  async (): Promise<DraftOrderAPIResponse | null> => {
    const response = await apiClient.get('/api/v1/orders/draft');

    return response.data;
    // if (orders.data.length) return orders.data[0];
    // return null;
  };

//orders
export const postOrder = async (
  body: PostDraftRequest,
  config?: AxiosRequestConfig,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.post(`/api/v1/orders`, body, config);

  return response.data;
};

export const submitOrder = async (
  orderId: string,
  body: { order_name?: string; invoice_id?: string },
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.post(
    `/api/v1/orders/${orderId}/submit`,
    body,
  );
  return response.data;
};

export const updateOrder = async (
  body: Omit<PostDraftRequest, 'status'>,
  orderId: string,
  config?: AxiosRequestConfig,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.patch(
    `/api/v1/orders/${orderId}`,
    body,
    config,
  );

  return response.data;
};

export const postQuote = async (
  body: CreateQuoteRequest,
  config?: AxiosRequestConfig,
) => {
  const response = await apiClient.post(`/api/v1/quotes/slim`, body, config);

  return response.data;
};

export const addOrderItems = async (
  body: PostOrderItems,
  locationId: string,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.patch(
    `/api/v1/orders/locations/${locationId}`,
    body,
  );

  return response.data;
};

export const clearAllOrderItems = async (
  orderId: string,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.patch(`/api/v1/orders/${orderId}/clear`);

  return response.data;
};

export const deleteOrder = async (
  orderId: string,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.delete(`/api/v1/orders/${orderId}`);
  return response.data;
};

export const createOrderItem = async (
  orderId: string,
  body: CreateOrderItem,
  config?: AxiosRequestConfig,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.post(
    `/api/v1/orders/${orderId}/items`,
    body,
    config,
  );
  return response.data;
};

export const moveOrderItems = async (
  locationId: string,
  body: MoveOrderItems,
  config?: AxiosRequestConfig,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.post(
    `/api/v1/orders/items/move/${locationId}`,
    body,
    config,
  );
  return response.data;
};

export const createLocationForOrder = async (
  orderId: string,
  body: CreateLocationForOrder,
  config?: AxiosRequestConfig,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.post(
    `/api/v1/orders/${orderId}/locations`,
    body,
    config,
  );
  return response.data;
};

export const updateOrderItem = async (
  itemId: string,
  body: UpdateOrderItem,
  config?: AxiosRequestConfig,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.patch(
    `/api/v1/orders/items/${itemId}`,
    body,
    config,
  );
  return response.data;
};

export const deleteOrderItem = async (
  orderId: string,
  itemId: string,
  config?: AxiosRequestConfig,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.delete(
    `/api/v1/orders/${orderId}/items/${itemId}`,
    config,
  );
  return response.data;
};

export const deleteOrderItems = async (
  orderId: string,
  config?: AxiosRequestConfig,
): Promise<DraftOrderAPIResponse> => {
  const response = await apiClient.delete(
    `/api/v1/orders/${orderId}/items/clear`,
    config,
  );
  return response.data;
};
